import { HttpError, httpErrorSchema, NormalizedError, normalizedMessageSchema } from '../../modules/backend/errors';
import isNil from '../helpers/is-nil';
import { Language } from '../i18n/constants';

export type Options = {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  bodyType?: 'json' | 'raw';
  headers?: HeadersInit;
  language: Language;
};

/**
 * This function is used to create a mutator for Tanstack query and should only be used for mutations.
 */
export const makeAnonymousMutator =
  <TData, TPayload>({ url, method, bodyType = 'json', headers, language }: Options) =>
  async (payload: TPayload): Promise<TData> => {
    const requestOptions: RequestInit = {
      method: method,
      headers: {
        'Accept-Language': language,
        'Content-Type': 'application/json',
        ...headers,
      },
      ...(!isNil(payload) && { body: JSON.stringify(payload) }),
    };

    const response = await fetch(url, requestOptions);

    // Tanstack query requires us to throw an error, if the request was not successful
    // https://tanstack.com/query/v4/docs/react/guides/query-functions#usage-with-fetch-and-other-clients-that-do-not-throw-by-default

    // don't try to parse the response if it is a 204
    if (response.status === 204) {
      return null as TData;
    }

    let data: unknown;

    try {
      if (bodyType === 'json') {
        data = await response.json();
      } else {
        data = response;
      }
    } catch (e) {
      throw new HttpError(response.statusText, response.status, String(e));
    }

    if (response.status === 400) {
      const result = normalizedMessageSchema.safeParse(data);
      if (result.success) {
        throw new NormalizedError(result.data.messages);
      }
    }

    if (!response.ok) {
      const result = httpErrorSchema.safeParse(data);
      if (!result.success) {
        throw new HttpError(response.statusText, response.status);
      }

      const { error, message, statusCode } = result.data;

      throw new HttpError(error, statusCode, message);
    }

    return data as TData;
  };
